import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import mapboxgl from "mapbox-gl";
import { useEmbeddedMap, type MapCamera } from "@/hooks/useEmbeddedMap";
import type { GeoCoordinates } from "@/lib/geocodedLocation";

interface LocationPickerMapProps {
  coordinates: GeoCoordinates | null;
  /** Click on the map or marker drag. */
  onPick: (coordinates: GeoCoordinates) => void;
  cameraRef: RefObject<MapCamera | null>;
  cooperativeGestures: boolean;
  ariaLabel: string;
}

const PICKED_ZOOM = 14;
const WORLD_CAMERA: MapCamera = { center: [0, 20], zoom: 1.5 };

function sameCoordinates(a: GeoCoordinates | null, b: GeoCoordinates): boolean {
  return a !== null && a.lat === b.lat && a.lng === b.lng;
}

/** Map with a draggable marker following `coordinates`; recenters on outside changes. */
export function LocationPickerMap({
  coordinates,
  onPick,
  cameraRef,
  cooperativeGestures,
  ariaLabel,
}: LocationPickerMapProps) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const map = useEmbeddedMap({
    container,
    cameraRef,
    initialCamera: coordinates
      ? { center: [coordinates.lng, coordinates.lat], zoom: PICKED_ZOOM }
      : WORLD_CAMERA,
    cooperativeGestures,
  });

  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  /** Coordinates the camera already shows: set at mount and on every pick. */
  const framedRef = useRef(coordinates);

  const pick = useCallback((picked: GeoCoordinates) => {
    framedRef.current = picked;
    onPickRef.current(picked);
  }, []);

  const lat = coordinates?.lat;
  const lng = coordinates?.lng;

  useEffect(() => {
    if (!map) return;
    const handleClick = (event: mapboxgl.MapMouseEvent) => {
      pick({ lat: event.lngLat.lat, lng: event.lngLat.lng });
    };
    map.on("click", handleClick);
    return () => {
      map.off("click", handleClick);
    };
  }, [map, pick]);

  useEffect(() => {
    if (!map || lat === undefined || lng === undefined) return;
    const current = { lat, lng };

    if (!sameCoordinates(framedRef.current, current)) {
      framedRef.current = current;
      map.jumpTo({
        center: [lng, lat],
        zoom: Math.max(map.getZoom(), PICKED_ZOOM),
      });
    }

    const marker = new mapboxgl.Marker({ draggable: true })
      .setLngLat([lng, lat])
      .addTo(map);
    marker.on("dragend", () => {
      const dropped = marker.getLngLat();
      pick({ lat: dropped.lat, lng: dropped.lng });
    });
    return () => {
      marker.remove();
    };
  }, [map, lat, lng, pick]);

  return <div ref={setContainer} className="h-full w-full" aria-label={ariaLabel} />;
}
